import { UserCoverageSummary } from '@ampsec/platform-client';
import {
  ENDPOINT_MANAGEMENT,
  ENDPOINT_SECURITY,
  VULNERABILITY_MANAGEMENT,
} from 'Core-utils/constants/constants';
import { DefaultTextCellRenderer } from '../AgGridBase';
import { assetDomainToTypeMap, getMinWidthForColumn } from '../AgToolingComplianceGrid/AgGridCompliance.config';
import { getEndpointsStatus } from '../AgToolingComplianceGrid/ToolingComplianceComponentHelper';
import AssetsCellRenderer from '../AgToolingComplianceGrid/cellRenderers/AssetsCellRenderer';
import DomainCellRenderer from '../AgToolingComplianceGrid/cellRenderers/DomainCellRenderer';
import AttributionStatusCellRenderer from '../AgToolingComplianceGrid/cellRenderers/AttributionStatusCellRenderer';

const USER_ASSET_COVERAGE_TOOLTIP_FIELDS = ['lastCheckin', 'status'] as const;

const getFirstAsset = (row: UserCoverageSummary | undefined) => row?.assets?.[0];

const ATTRIBUTION_CONFLICT_LABEL = 'Conflict';
const ATTRIBUTION_NO_CONFLICT_LABEL = 'No Conflict';

const hasAttributionConflict = (row: UserCoverageSummary): boolean => {
  if (!Array.isArray(row.assets)) {
    return false;
  }
  // Consider every asset so the column value matches the shared cell renderer (which
  // inspects all of row.assets) even if a row ever carries more than one asset.
  return row.assets.some((asset: any) =>
    asset?.attributionStatus === 'NEGATIVE' ||
    (Array.isArray(asset?.saasAssets) &&
      asset.saasAssets.some((saasAsset: any) => saasAsset?.attributionStatus === 'NEGATIVE'))
  );
};

const userAssetCoverageColumns = [
  {
    field: 'endpoints',
    colId: 'endpoints',
    flex: 1,
    headerName: 'Asset',
    minWidth: getMinWidthForColumn('endpoints'),
    filter: false,
    cellRenderer: AssetsCellRenderer,
    cellRendererParams: {
      tooltipFields: USER_ASSET_COVERAGE_TOOLTIP_FIELDS,
    },
    valueGetter: (params: { data: UserCoverageSummary }) => getFirstAsset(params.data)?.displayValue ?? '',
  },
  {
    colId: 'hostname',
    headerName: 'Host Name',
    minWidth: 160,
    filter: false,
    valueGetter: (params: { data: UserCoverageSummary }) => getFirstAsset(params.data)?.hostname ?? '',
    cellRenderer: DefaultTextCellRenderer,
  },
  {
    colId: 'serialNumber',
    headerName: 'Serial Number',
    minWidth: 120,
    width: 120,
    filter: false,
    valueGetter: (params: { data: UserCoverageSummary }) => getFirstAsset(params.data)?.sn ?? '',
    cellRenderer: DefaultTextCellRenderer,
  },
  {
    field: 'attributionStatus',
    colId: 'attributionStatus',
    headerName: 'Attribution Status',
    minWidth: getMinWidthForColumn('attributionStatus'),
    width: 120,
    filter: false,
    cellRenderer: AttributionStatusCellRenderer,
    valueGetter: (params: { data: UserCoverageSummary }) =>
      hasAttributionConflict(params.data) ? ATTRIBUTION_CONFLICT_LABEL : ATTRIBUTION_NO_CONFLICT_LABEL,
  },
  {
    colId: 'osVersion',
    headerName: 'OS Version',
    minWidth: 140,
    flex: 1,
    filter: false,
    valueGetter: (params: { data: UserCoverageSummary }) => getFirstAsset(params.data)?.os ?? '',
    cellRenderer: DefaultTextCellRenderer,
  },
  {
    colId: 'model',
    headerName: 'Model',
    minWidth: 140,
    flex: 1,
    filter: false,
    valueGetter: (params: { data: UserCoverageSummary }) => getFirstAsset(params.data)?.model ?? '',
    cellRenderer: DefaultTextCellRenderer,
  },
  {
    field: 'endpointManagement',
    colId: 'endpointManagement',
    headerName: 'Endpoint Mgmt',
    minWidth: getMinWidthForColumn('endpointManagement'),
    width: getMinWidthForColumn('endpointManagement'),
    sortable: false,
    filter: false,
    cellRenderer: DomainCellRenderer,
    cellRendererParams: {
      domain: ENDPOINT_MANAGEMENT,
      domainType: assetDomainToTypeMap[ENDPOINT_MANAGEMENT],
      isAssetCell: true,
    },
    valueGetter: (params: { data: UserCoverageSummary; context: unknown }) =>
      `Providers: ${getEndpointsStatus(params.data?.assets ?? [], ENDPOINT_MANAGEMENT, params.context as any)}`,
  },
  {
    field: 'endpointSecurity',
    colId: 'endpointSecurity',
    headerName: 'Endpoint Security',
    minWidth: getMinWidthForColumn('endpointSecurity'),
    width: getMinWidthForColumn('endpointSecurity'),
    sortable: false,
    filter: false,
    cellRenderer: DomainCellRenderer,
    cellRendererParams: {
      domain: ENDPOINT_SECURITY,
      domainType: assetDomainToTypeMap[ENDPOINT_SECURITY],
      isAssetCell: true,
    },
    valueGetter: (params: { data: UserCoverageSummary; context: unknown }) =>
      `Providers: ${getEndpointsStatus(params.data?.assets ?? [], ENDPOINT_SECURITY, params.context as any)}`,
  },
  {
    field: 'vulnerabilityManagement',
    colId: 'vulnerabilityManagement',
    headerName: 'Vulnerability Mgmt',
    minWidth: getMinWidthForColumn('vulnerabilityManagement'),
    width: getMinWidthForColumn('vulnerabilityManagement'),
    sortable: false,
    filter: false,
    cellRenderer: DomainCellRenderer,
    cellRendererParams: {
      domain: VULNERABILITY_MANAGEMENT,
      domainType: assetDomainToTypeMap[VULNERABILITY_MANAGEMENT],
      isAssetCell: true,
    },
    valueGetter: (params: { data: UserCoverageSummary; context: unknown }) =>
      getEndpointsStatus(params.data?.assets ?? [], VULNERABILITY_MANAGEMENT, params.context as any),
  },
];

export default userAssetCoverageColumns;
